import { Injectable } from '@angular/core';
import { BehaviorSubject, delay, combineLatest, map } from 'rxjs';

export type LoadingScope = 'global' | 'grid' | 'input' | 'button';

@Injectable({ providedIn: 'root' })
export class EnhancedLoadingService {
  // Contadores por escopo
  private counts = {
    global: 0,
    grid: 0,
    input: 0,
    button: 0,
  };

  // Observables por escopo
  private subjects = {
    global: new BehaviorSubject<boolean>(false),
    grid: new BehaviorSubject<boolean>(false),
    input: new BehaviorSubject<boolean>(false),
    button: new BehaviorSubject<boolean>(false),
  };

  // Streams públicos
  readonly global$ = this.subjects.global.asObservable().pipe(delay(0));
  readonly grid$ = this.subjects.grid.asObservable().pipe(delay(0));
  readonly input$ = this.subjects.input.asObservable().pipe(delay(0));
  readonly button$ = this.subjects.button.asObservable().pipe(delay(0));

  // Para compatibilidade com código existente
  readonly loading$ = this.global$;

  // Observable que retorna true se qualquer escopo estiver carregando
  readonly isLoad$ = combineLatest([
    this.global$,
    this.grid$,
    this.input$,
    this.button$,
  ]).pipe(
    map(([global, grid, input, button]) => global || grid || input || button),
  );

  show(scope: LoadingScope = 'global') {
    this.counts[scope]++;
    this.subjects[scope].next(true);
  }

  hide(scope: LoadingScope = 'global') {
    this.counts[scope] = Math.max(this.counts[scope] - 1, 0);
    if (this.counts[scope] === 0) {
      this.subjects[scope].next(false);
    }
  }

  // Método para tarefas longas
  showWithTimeout(scope: LoadingScope = 'global', timeout: number = 5000) {
    this.show(scope);
    const timeoutId = setTimeout(() => {
      // Converter para loading de progresso se ainda ativo
      if (this.counts[scope] > 0) {
        this.onLongTask(scope);
      }
    }, timeout);

    return () => {
      clearTimeout(timeoutId);
      this.hide(scope);
    };
  }

  private onLongTask(scope: LoadingScope) {
    console.log(`Tarefa longa detectada no escopo: ${scope}`);
  }
}
